import { z } from "zod";

import {
  assetSchema,
  availabilitySchema,
  userSchema,
  workingHoursSchema,
} from "../commons";

export const designerTestimonialSchema = z.object({
  company: z.string().optional(),
  csat: z.number(),
  logo: z.string().optional(),
  name: z.string(),
  text: z.string().optional(),
  title: z.string(),
  designerId: z.string().optional(),
});

export type DesignerTestimonial = z.infer<typeof designerTestimonialSchema>;

export const designerSchema = z.object({
  _id: z.string(),
  avatar: z.string(),
  email: z.string(),
  hero: assetSchema,
  hourlyRate: z.number().optional(),
  isAvailable: z.boolean().optional(),
  isPortfolioActive: z.boolean(),
  name: z.string(),
  skills: z.array(z.string()),
  tools: z.array(z.string()),
  testimonial: designerTestimonialSchema.optional(),
  availability: availabilitySchema.optional(),
  title: z.string().optional(),
  website: z.string(),
  level: z.number().optional(),
});

export type Designer = z.infer<typeof designerSchema>;

// @deprecated
export const searchableDesignerSchema = z.object({
  _id: z.string(),
  name: z.string(),
  avatar: z.string(),
  hero: assetSchema,
  timezone: z.string(),
  workingHours: workingHoursSchema.optional(),
  email: z.string(),
  level: z.number().optional(),
  hourlyRate: z.number().optional(),
  isAvailable: z.boolean(),
  availability: z.union([availabilitySchema, z.string()]),
  isPortfolioActive: z.boolean(),
  industries: z.array(z.string()),
  skills: z.array(z.string()),
  specialties: z.array(z.string()),
  tools: z.array(z.string()),
  title: z.string().optional(),
  website: z.string().optional(),
});

export type SearchableDesigner = z.infer<typeof searchableDesignerSchema>;

export const getResponseSchema = z.array(designerSchema);

export type GetResponse = z.infer<typeof getResponseSchema>;

export const getSearchableResponseSchema = z.array(searchableDesignerSchema);

export type GetSearchableResponse = z.infer<typeof getSearchableResponseSchema>;

// Search V2
export const searchResult = userSchema
  .pick({
    _id: true,
    name: true,
    avatar: true,
    hero: true,
    specialties: true,
    level: true,
    website: true,
  })
  .required()
  .extend({
    isAvailable: z.boolean(),
    isPortfolioActive: z.boolean(),
  });

export type SearchResult = z.infer<typeof searchResult>;

export const searchDesignerBodySchema = z
  .object({
    query: z.string().optional(),
    rate: z.array(z.number()).optional(),
    availability: z.array(availabilitySchema).optional(),
    skills: z.array(z.string()).optional(),
    industries: z.array(z.string()).optional(),
    tools: z.array(z.string()).optional(),
    specialties: z.array(z.string()).optional(),
    pageSize: z.number().optional(),
    pageNumber: z.number().optional(),
    searchOperator: z.string().optional(),
  })
  .optional();

export type SearchDesignerBody = z.infer<typeof searchDesignerBodySchema>;

export const searchDesignerRequestSchema = z.object({
  body: searchDesignerBodySchema,
});

export const foundTagsSchema = z.object({
  skills: z.array(z.string()),
  industries: z.array(z.string()),
  tools: z.array(z.string()),
  specialties: z.array(z.string()),
});

export const searchDesignersResponseSchema = z.object({
  creatives: z.array(searchResult),
  total: z.coerce.number(),
  foundTags: foundTagsSchema,
});

export type SearchDesignersResponse = z.infer<
  typeof searchDesignersResponseSchema
>;
